import { useModalQueue } from '@carvana/merch-react-hooks';
import {
  MoreDetailsModalSections,
  MoreDetailsOptions
} from '@carvana/merch-tile';
import deepmerge from 'deepmerge';
import { createContext, useCallback, useMemo, useState } from 'react';
import type { AppliedFilters } from '~/contracts/common';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type WIPModalOptions = {
  vehicleId: number;
};

type ContextType = {
  filtersToSave: AppliedFilters;
  isActivationModalOpen: boolean;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAppraisalRefreshModalOpen: boolean;
  isCreateSavedSearchModalOpen: boolean;
  isCreditKarmaPromoModalOpen: boolean;
  isEVTaxCreditModalOpen: boolean;
  isFilterModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isManageSavedSearchesModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isSaveSearchModalOpen: boolean;
  isSaveSearchNudgeOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  isWipNotificationModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  setActivationModalOpen: (isOpen: boolean) => void;
  setAddCoApplicantModalOpen: (isOpen: boolean) => void;
  setAppraisalRefreshModalOpen: (isOpen: boolean) => void;
  setCreateSavedSearchOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setCreditKarmaPromoModalOpen: (isOpen: boolean) => void;
  setEVTaxCreditModalOpen: (isOpen: boolean) => void;
  setFilterModalOpen: (isOpen: boolean) => void;
  setGeolocationModalOpen: (isOpen: boolean) => void;
  setIsSearchBarFocused: (isFocused: boolean) => void;
  setManageSavedSearchesOpen: (isOpen: boolean) => void;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
  setSaveSearchModalOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setSaveSearchNudgeOpen: (isOpen: boolean) => void;
  setSearchBarModalOpen: (isOpen: boolean) => void;
  setSoftPullModalOpen: (isOpen: boolean) => void;
  setWipNotificationModalOpen: (
    isOpen: boolean,
    options?: WIPModalOptions
  ) => void;
  wipModalVehicleId: number;
};

export const defaultContext: ContextType = {
  filtersToSave: {},
  isActivationModalOpen: false,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isAppraisalRefreshModalOpen: false,
  isCreateSavedSearchModalOpen: false,
  isCreditKarmaPromoModalOpen: false,
  isEVTaxCreditModalOpen: false,
  isFilterModalOpen: false,
  isGeolocationModalOpen: false,
  isManageSavedSearchesModalOpen: false,
  isMoreDetailsModalOpen: false,
  isSaveSearchModalOpen: false,
  isSaveSearchNudgeOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isSoftPullModalOpen: false,
  isWipNotificationModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails
  },
  setActivationModalOpen() {},
  setAddCoApplicantModalOpen() {},
  setAppraisalRefreshModalOpen() {},
  setCreateSavedSearchOpen() {},
  setCreditKarmaPromoModalOpen() {},
  setEVTaxCreditModalOpen() {},
  setFilterModalOpen() {},
  setGeolocationModalOpen() {},
  setIsSearchBarFocused() {},
  setManageSavedSearchesOpen() {},
  setMoreDetailsModalOpen() {},
  setSaveSearchModalOpen() {},
  setSaveSearchNudgeOpen() {},
  setSearchBarModalOpen() {},
  setSoftPullModalOpen() {},
  setWipNotificationModalOpen() {},
  wipModalVehicleId: 0
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [wipModalVehicleId, setWipModalVehicleId] = useState(
    defaultContext.wipModalVehicleId
  );

  const {
    setActivationModalOpen,
    setAppraisalRefreshModalOpen,
    setCreditKarmaPromoModalOpen,
    setAddCoApplicantModalOpen,
    setCreateSavedSearchOpen,
    setEVTaxCreditModalOpen,
    setGeolocationModalOpen,
    setManageSavedSearchesOpen,
    setMoreDetailsModalOpen,
    setSaveSearchModalOpen,
    setSaveSearchNudgeOpen,
    setSearchBarModalOpen,
    setSoftPullModalOpen,
    setWipNotificationModalOpen
  } = useMemo(
    () => ({
      setActivationModalOpen: generateOpener(ModalId.Activation),
      setAppraisalRefreshModalOpen: generateOpener(ModalId.AppraisalRefresh),
      setCreditKarmaPromoModalOpen: generateOpener(ModalId.CkPromo),
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setCreateSavedSearchOpen: generateOpener(ModalId.CreateSavedSearch),
      setEVTaxCreditModalOpen: generateOpener(ModalId.EVTaxCredit),
      setGeolocationModalOpen: generateOpener(ModalId.Geolocation),
      setManageSavedSearchesOpen: generateOpener(ModalId.ManageSavedSearches),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setSaveSearchModalOpen: generateOpener(ModalId.SaveSearchModal),
      setSaveSearchNudgeOpen: generateOpener(ModalId.SaveSearchNudge),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setWipNotificationModalOpen: generateOpener(ModalId.WIPNotifications)
    }),
    [generateOpener]
  );

  const setCreateSavedSearchOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: Partial<AppliedFilters>) => {
      setFiltersToSave(filters);
      setCreateSavedSearchOpen(isOpen);
    },
    [setCreateSavedSearchOpen]
  );

  const setSavedSearchModalOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: Partial<AppliedFilters>) => {
      setFiltersToSave(filters);
      setSaveSearchModalOpen(isOpen);
    },
    [setSaveSearchModalOpen]
  );

  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (isOpen: boolean, options?: MoreDetailsOptions) => {
      if (options) setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen);
    },
    [setMoreDetailsModalOpen]
  );

  const setWipNotificationModalOpenWithId = useCallback<
    ContextType['setWipNotificationModalOpen']
  >(
    (isOpen: boolean, options?: WIPModalOptions) => {
      if (options) setWipModalVehicleId(options.vehicleId);
      setWipNotificationModalOpen(isOpen);
    },
    [setWipNotificationModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      filtersToSave: filtersToSave,
      isActivationModalOpen: currentModalId === ModalId.Activation,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1 || isFilterModalOpen,
      isAppraisalRefreshModalOpen: currentModalId === ModalId.AppraisalRefresh,
      isCreateSavedSearchModalOpen:
        currentModalId === ModalId.CreateSavedSearch,
      isCreditKarmaPromoModalOpen: currentModalId === ModalId.CkPromo,
      isEVTaxCreditModalOpen: currentModalId === ModalId.EVTaxCredit,
      isFilterModalOpen,
      isGeolocationModalOpen: currentModalId === ModalId.Geolocation,
      isManageSavedSearchesModalOpen:
        currentModalId === ModalId.ManageSavedSearches,
      isMoreDetailsModalOpen: currentModalId === ModalId.MoreDetails,
      isSaveSearchModalOpen: currentModalId === ModalId.SaveSearchModal,
      isSaveSearchNudgeOpen: currentModalId === ModalId.SaveSearchNudge,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isWipNotificationModalOpen: currentModalId === ModalId.WIPNotifications,
      moreDetailsOptions,
      setActivationModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setCreateSavedSearchOpen: setCreateSavedSearchOpenWithFiltersToSave,
      setCreditKarmaPromoModalOpen,
      setEVTaxCreditModalOpen,
      setFilterModalOpen,
      setGeolocationModalOpen,
      setIsSearchBarFocused,
      setManageSavedSearchesOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setSaveSearchModalOpen: setSavedSearchModalOpenWithFiltersToSave,
      setSaveSearchNudgeOpen,
      setSearchBarModalOpen,
      setSoftPullModalOpen,
      setWipNotificationModalOpen: setWipNotificationModalOpenWithId,
      wipModalVehicleId
    }),
    [
      filtersToSave,
      currentModalId,
      isFilterModalOpen,
      isSearchBarFocused,
      moreDetailsOptions,
      setActivationModalOpen,
      setAppraisalRefreshModalOpen,
      setCreditKarmaPromoModalOpen,
      setAddCoApplicantModalOpen,
      setCreateSavedSearchOpenWithFiltersToSave,
      setEVTaxCreditModalOpen,
      setGeolocationModalOpen,
      setManageSavedSearchesOpen,
      setMoreDetailsModalOpenWithVehicle,
      setSavedSearchModalOpenWithFiltersToSave,
      setSaveSearchNudgeOpen,
      setSearchBarModalOpen,
      setSoftPullModalOpen,
      setWipNotificationModalOpenWithId,
      wipModalVehicleId
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);
